import React, { useState, useMemo, Fragment } from 'react';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from 'hooks/apollo';
import { FormattedMessage, FormattedDate, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { Tag, Tooltip, Button, Input, Space, Divider } from 'antd';
import { FileAddOutlined, FormOutlined, SearchOutlined, WarningOutlined, TranslationOutlined, SecurityScanOutlined } from '@ant-design/icons';
import appTheme from 'shared/constants/theme.json';
import { SurveyType } from 'shared/prisma/types';
import { uniqueArray } from 'shared/utils/unique';
import { segmentKey, filterSummaryResponses, summarySegments } from 'shared/utils/textResponseSummaryScope';
import { path } from 'shared/utils/url';

import { AUTO_TAG, EDIT_TAGGED_COMMENT, EDIT_FLAGGED_COMMENT } from 'shared/constants/permissions';
import { openGoogleTranslate } from 'components/TranslationButton';
import { formatSegment } from 'components/Charts/formatters';
import Modal from 'components/Modal';
import Table from 'components/Table';
import { arraySorter } from 'components/Table/sorters';
import DownloadTextResponses from 'components/DownloadTextResponses';
import WordCloudChart from 'components/Charts/WordCloudChart';
import ChartSubtitles from 'components/Charts/ChartSubtitles';
import LicenseTag from 'components/LicenseTag';
import Brain from 'components/Icons/Brain';

import useGlobalContext from 'hooks/useGlobalContext';
import useDataSource from 'hooks/useDataSource';
import useUser from 'hooks/useUser';

import globalMessages from 'translations/messages';
import TagComments from './TagComments';
import messages from './messages';

import './field-results.less';
import ThemeResult from './ThemeResult';
import SentimentResult from './SentimentResult';
import { TextResponseSummaries } from './TextResponseSummary';

const PRINT_LIMIT = 50;
const WEB_LIMIT = 1000;

const QUERY = gql`
  query($surveyIds: [ID!]!, $responseIds: [ID!]!) {
    questionResponses(surveyIds: $surveyIds, responseIds: $responseIds, includeKeywords: true) {
      id
      text
      redactedAt
      piiScan { updatedAt }
      FlaggedComment { id }
      surveyResponse {
        id
        respondedAt     
        webpageUrl   
        webpageTitle
      }
      tags {
        id
        type
        text
      }      
      autoTag {
        id
        tags { id text }
        sentiments { id text }
        translation
      }
      keywords
    }
  }
`;

const REPORT_COMMENT = gql`
  mutation($questionResponseId: ID!) {
    reportComment(questionResponseId: $questionResponseId)
  }
`;

const prettifyRedactedText = (text) => <span dangerouslySetInnerHTML={{ __html: text?.replaceAll(/{{([^}]*)}}/g, '<em>*$1*</em>') }} />;

const CommentTag = ({ tag, translate, onClick }) => {
  const { formatMessage } = useIntl();
  const name = translate && messages[tag.text] ? formatMessage(messages[tag.text]) : tag.text;

  return (
    <Tag
      color={appTheme[tag.text]}
      style={{ marginTop: 4 }}
      onClick={() => onClick({ id: tag.text, name: tag.text, type: tag.type })}
      icon={tag.autoTag ? <Brain /> : undefined}
      className="clickable"
    >
      {name}
    </Tag>
  );
};

const getTextSearchProps = (handleSearch, handleReset) => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }}>
      <Input
        placeholder="Search"
        value={selectedKeys[0]}
        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
        onPressEnter={() => handleSearch(selectedKeys, confirm)}
        style={{ width: 188, marginBottom: 8, display: 'block' }}
      />
      <Space>
        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
          <FormattedMessage {...globalMessages.reset} />
        </Button>
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm)}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90 }}
        >
          <FormattedMessage {...globalMessages.search} />
        </Button>

      </Space>
    </div>
  ),
  onFilter: (value, record) => record.text?.toString().toLowerCase().includes(value.toLowerCase()),
});

const renderSegmentIndicators = (segments) => (
  <div className="text-responses__table--segments-indicators">
    {segments.map(s => (
      <span
        key={s.color}
        style={{
          borderRadius: 2,
          height: 10,
          width: 10,
          backgroundColor: s.color,
        }}
      />
    ))}
  </div>
);

const renderSegmentTags = (segments) => segments.map(s => (
  <Tag
    key={s.segmentId}
    color={s.color}
    style={{ marginTop: 4 }}
  >
    {s.name}
  </Tag>
));

const renderThemes = onClick => themes => themes?.map(tag => <CommentTag key={tag.text} tag={tag} onClick={onClick} />);

const renderSentiments = onClick => sentiments => sentiments?.map(tag => <CommentTag key={tag.text} tag={tag} onClick={onClick} translate />);

const tableColumns = ({
  segments,
  themes,
  withoutTheme,
  sentiments,
  withoutSentiment,
  formatMessage,
  locale,
  isReport,
  isPrint,
  handleSearch,
  handleReset,
  onTagClick,
}) => {
  const columns = [
    {
      dataIndex: 'segments',
      title: <FormattedMessage {...globalMessages.segments} />,
      render: record => (isReport ? renderSegmentTags(record) : renderSegmentIndicators(record)),
      filters: !isPrint && segments.map(s => ({
        text: formatSegment(s, { formatMessage, locale }),
        value: segmentKey(s),
      })),
      onFilter: (value, record) => record.segments?.some(s => segmentKey(s) === value),
      defaultFilteredValue: segments.filter(s => s.dateRangeIndex === 0).map(segmentKey),
      width: 100,
      inactive: segments.length <= 1,
    },
    {
      dataIndex: ['surveyResponse', 'respondedAt'],
      title: <FormattedMessage {...globalMessages.date} />,
      render: date => <FormattedDate value={date} year="numeric" day="numeric" month="short" hour="numeric" minute="numeric" />,
      width: 160,
      defaultSortOrder: 'descend',
    },
    {
      dataIndex: 'text',
      title: <FormattedMessage {...globalMessages.text} />,
      render: (text, record) => {
        const html = prettifyRedactedText(text);
        const translation = record.autoTag?.translation ? prettifyRedactedText(record.autoTag?.translation) : null;
        if (translation) {
          return <Tooltip title={translation}>{html}</Tooltip>;
        }
        return html;
      },
      ...(isPrint ? {} : getTextSearchProps(handleSearch, handleReset)),
    },
    {
      dataIndex: ['autoTag', 'translation'],
      title: (
        <>
          <Brain style={{ marginRight: 4 }} />
          <FormattedMessage {...globalMessages.translation} />
        </>
      ),
      render: translation => <span dangerouslySetInnerHTML={{ __html: translation }} />,
      visible: false,
    },
    {
      dataIndex: ['surveyResponse', 'webpageUrl'],
      title: 'Response page URL',
      visible: false,
      render: url => <Tooltip title={url}>{path(url)}</Tooltip>,
    },
    {
      dataIndex: ['surveyResponse', 'webpageTitle'],
      title: 'Response page title',
      visible: false,
    },
    {
      title: <FormattedMessage {...messages.theme} />,
      width: 200,
      dataIndex: 'themes',
      render: renderThemes(onTagClick),
      filters: !isPrint && [{ text: <i>{`Without tag (${withoutTheme})`}</i>, value: false }, ...themes.map(t => ({
        text: `${t.text} (${t.count})`,
        value: t.text,
      }))],
      onFilter: (value, record) => (value ? record.themes?.find(t => t.text === value) : !record.themes?.length),
      inactive: !themes.length,
      sorter: arraySorter('themes', 'text'),
    },
    {
      title: <FormattedMessage {...messages.sentiment} />,
      width: 110,
      dataIndex: 'sentiments',
      render: renderSentiments(onTagClick),
      filters: !isPrint && [{ text: <i>{`Without sentiment (${withoutSentiment})`}</i>, value: false }, ...sentiments.map(t => ({
        text: `${formatMessage(messages[t.text])} (${t.count})`,
        value: t.text,
      }))],
      onFilter: (value, record) => (value ? record.sentiments?.find(t => t.text === value) : !record.themes?.length),
      inactive: !sentiments.length,
      sorter: arraySorter('sentiments', 'text'),
    },
  ];

  return columns;
};

function countTags(tagsWithCount, tags, idProp = 'text', countProp = 'count') {
  for (const tag of tags) {
    const existingTag = tagsWithCount.find(t => t[idProp] === tag[idProp]);
    if (existingTag) {
      existingTag[countProp] += tag[countProp];
    } else {
      tagsWithCount.push({
        ...tag,
      });
    }
  }
}

function formatData(segments, data, aiDisabled) {
  const keywords = [];
  const themes = [];
  const sentiments = [];

  for (const segment of segments) {
    if (!aiDisabled && segment.themes) {
      countTags(themes, segment.themes);
    }
    if (!aiDisabled && segment.sentiments) {
      countTags(sentiments, segment.sentiments);
    }
  }

  const responses = data?.questionResponses.map(response => {
    const r = aiDisabled ? { ...response, autoTag: null } : response;
    countTags(keywords, r.keywords.map(k => ({ name: k, value: 1 })), 'name', 'value');

    const responseSegments = segments.filter(s => s.values.includes(r.id));
    const responseThemes = r.tags.filter(t => t.type === 'THEME');
    responseThemes.push(...(r.autoTag?.tags.filter(t => !responseThemes.find(rt => rt.id === t.id)).map(t => ({ ...t, type: 'THEME', autoTag: true })) || []));
    const responseSentiments = r.tags.filter(t => t.type === 'SENTIMENT');
    responseSentiments.push(...(r.autoTag?.sentiments.filter(t => !responseSentiments.find(rs => rs.id === t.id)).map(t => ({ ...t, type: 'SENTIMENT', autoTag: true })) || []));

    return ({
      ...r,
      text: r.text ? r.text.trim() : '',
      segments: responseSegments,
      themes: responseThemes,
      sentiments: responseSentiments,
    });
  });

  return {
    keywords,
    themes: themes.filter(t => t.count > 0).sort((a, b) => b.count - a.count),
    sentiments: sentiments.filter(t => t.count > 0),
    responses,
  };
}

const FieldResult = ({
  title,
  subTitles,
  periods,
  question,
  surveyGroupId,
  displayedSurvey,
  addToReport,
  isReport,
  reportSnapshot,
  includeAISummary = false,
  summaryOnly = false,
  dataSourceId,
  isPrint,
  refetchChartResults,
  allowPersonalData,
  width,
  onSelectChartSegment,
  chartSettings,
  onChartSettingsChanged,
}) => {
  const { hasDataSourcePermission, getDataSourceLicenseSetting, aiDisabled: accountAIDisabled } = useDataSource({ dataSourceId, skip: isPrint, fetchPolicy: 'cache-and-network' });
  const { isSuperUser } = useUser();
  const aiDisabled = accountAIDisabled || question.aiDisabled;
  const reportPreview = isReport && !isPrint && (isSuperUser || getDataSourceLicenseSetting('aiSummaries') === true) && hasDataSourcePermission(AUTO_TAG);
  const [previewSession] = useState(() => `${Date.now()}:${Math.random()}`);
  const { formatMessage, locale } = useIntl();
  const [modalOpen, setModalOpen] = useState(false);
  const globalContext = useGlobalContext();

  const { segments } = question;

  const surveyIds = useMemo(() => uniqueArray(segments.flatMap(s => s.surveyIds)), [segments]);
  const responseIds = useMemo(() => uniqueArray(segments.flatMap(s => s.values)).sort(), [segments]);

  const limit = isPrint ? PRINT_LIMIT : WEB_LIMIT;

  const { refetch, loading, fetching, data } = useQuery(QUERY, {
    skip: summaryOnly,
    variables: {
      surveyIds,
      responseIds: responseIds.slice(0, limit),
    },
  });

  const { keywords, themes, sentiments, responses } = useMemo(() => formatData(segments, data, aiDisabled), [question, data, aiDisabled]);

  const [tableFilters, setTableFilters] = useState({});

  const [reportCommentMutation] = useMutation(REPORT_COMMENT);

  const chartTitle = title || question.title;

  const handleSearch = (selectedKeys, confirm) => {
    confirm();
  };

  const handleReset = clearFilters => {
    clearFilters({ confirm: true, closeDropdown: true });
  };

  const reportComment = record => {
    globalContext.modal?.confirm({
      title: formatMessage(messages[allowPersonalData ? 'reportCommentPersonalDataAllowed' : 'reportComment']),
      icon: <SecurityScanOutlined />,
      content: (
        <div>
          <p>{formatMessage(messages.reportCommentContent)}</p>
          <blockquote>{record.text}</blockquote>
        </div>
      ),
      onOk: async () => {
        await reportCommentMutation({
          variables: {
            questionResponseId: record.id,
          },
        });
        refetchChartResults();
      },
    });
  };

  const showWordCloud = !summaryOnly && ((!isReport && keywords.length > 0) || chartSettings?.fieldResultType === 'wordcloud');
  const showThemeChart = !summaryOnly && ((!isReport && themes.length > 0) || chartSettings?.fieldResultType === 'theme');
  const showSentimentChart = !summaryOnly && ((!isReport && sentiments.length > 0) || chartSettings?.fieldResultType === 'sentiment');
  const showTable = !summaryOnly && (!isReport || !(showWordCloud || showThemeChart || showSentimentChart));

  const onTagClick = (tag) => {
    onSelectChartSegment?.({ data: tag, question });
  };

  const chartProps = {
    question,
    segments,
    periods,
    width,
    onSelectChartSegment,
    addToReport,
    chartSettings,
    onChartSettingsChanged,
    isReport,
    isPrint,
  };

  const tagCommentsEnabled = getDataSourceLicenseSetting('tagComments') !== false;
  const canTagComments = tagCommentsEnabled && hasDataSourcePermission(EDIT_TAGGED_COMMENT);

  const columns = useMemo(() => tableColumns({
    segments,
    themes,
    withoutTheme: responses?.filter(r => !r.themes?.length).length,
    sentiments,
    withoutSentiment: responses?.filter(r => !r.sentiments?.length).length,
    handleSearch,
    handleReset,
    isPrint,
    isReport,
    formatMessage,
    locale,
    onTagClick,
  }).filter(column => !aiDisabled || column.dataIndex?.[0] !== 'autoTag'), [segments, themes, sentiments, responses, aiDisabled]);

  const currentDataSource = filterSummaryResponses(responses, columns, tableFilters);
  const defaultSegments = columns.find(column => column.dataIndex === 'segments');
  const selectedSegments = Object.hasOwn(tableFilters, 'segments') ? tableFilters.segments : defaultSegments?.defaultFilteredValue;
  const summaries = summarySegments(segments, isReport ? (responses || []) : currentDataSource, isReport ? null : selectedSegments);

  const actionsMenuItems = record => [{
    key: 'reportComment',
    label: <FormattedMessage {...messages[allowPersonalData ? 'reportCommentPersonalDataAllowed' : 'reportComment']} />,
    onClick: () => reportComment(record),
    icon: <WarningOutlined />,
    visible: !isReport,
  }, {
    key: 'translate',
    label: <FormattedMessage {...messages.translateComment} />,
    onClick: () => openGoogleTranslate({ text: record.text }),
    icon: <TranslationOutlined />,
  }];

  const content = [];

  if (showWordCloud) {
    content.push(<WordCloudChart key="wordcloud" data={keywords} addToReport={addToReport} />);
  }

  if (showThemeChart && !aiDisabled) {
    content.push(<ThemeResult key="theme" {...chartProps} />);
  }

  if (showSentimentChart && !aiDisabled) {
    content.push(<SentimentResult key="sentiment" {...chartProps} />);
  }

  if (showTable) {
    content.push(<Table
      className="text-responses__table break-word"
      size={isPrint ? 'small' : 'middle'}
      pagination={isPrint ? { pageSize: PRINT_LIMIT, showSizeChanger: false } : undefined}
      emptyMessage={<FormattedMessage {...messages[isReport ? 'noMatchingDataReport' : 'noMatchingData']} />}
      bordered
      columns={columns}
      interactive={!isPrint}
      dataSource={responses}
      loading={loading}
      actionsMenuItems={actionsMenuItems}
      footer={() => (
        <>
          {question.flaggedCommentsCount > 0 && (
            <Space>
              <FormattedMessage
                {...messages[displayedSurvey?.type === SurveyType.General ? 'flaggedCommentsCountQuestionnaire' : 'flaggedCommentsCount']}
                values={{ count: question.flaggedCommentsCount }}
              />
              {!isPrint && hasDataSourcePermission(EDIT_FLAGGED_COMMENT) && (
                <Link to={`/administration/flagged-comments?surveyGroupId=${surveyGroupId}`}>
                  <FormattedMessage {...messages.manageFlaggedComments} />
                </Link>
              )}
            </Space>
          )}
          {responseIds.length > limit && (
            <div>
              <FormattedMessage {...messages.exceededLimit} values={{ limit, total: responseIds.length }} />
            </div>
          )}
        </>
      )}
      footerActions={!isReport && (
        <>
          {!tagCommentsEnabled && <LicenseTag setting="tagComments" showTooltip />}
          <Space.Compact>
            <Tooltip title={<FormattedMessage {...messages.tagComments} />}>
              <Button icon={<FormOutlined />} onClick={() => setModalOpen(true)} disabled={!canTagComments || !responses?.length} />
            </Tooltip>
            <DownloadTextResponses
              title={question.title}
              responses={currentDataSource || responses}
              themes={themes}
              sentiments={sentiments}
            />
            {addToReport && (
              <Tooltip
                title={<FormattedMessage {...messages.addToReport} />}
              >
                <Button
                  icon={<FileAddOutlined />}
                  onClick={addToReport}
                />
              </Tooltip>
            )}
          </Space.Compact>
        </>
      )}
      onChange={(pagination, filters) => setTableFilters(filters)}
    />);
  }

  return (
    <>
      <Modal
        title={<span dangerouslySetInnerHTML={{ __html: `${formatMessage(messages.tagComments)} - ${chartTitle}` }} />}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        fullWidth
        maxHeight
        centered
        maskClosable={false}
        footer={null}
        afterClose={refetchChartResults}
        destroyOnClose
      >
        {modalOpen && (
          <TagComments
            question={question}
            reportComment={reportComment}
            responses={responses}
            responsesLoading={loading}
            refetchResponses={refetch}
            displayedSurvey={displayedSurvey}
            surveyIds={surveyIds}
            responseIds={responseIds}
          />
        )}
      </Modal>

      <ChartSubtitles subTitles={[subTitles]} />

      {!aiDisabled && (!isReport || includeAISummary) && !fetching && (
        <TextResponseSummaries
          isReport={isReport}
          summaries={summaries.map(({ segment, key, responseIds: ids, revision }) => ({
            key: isReport ? JSON.stringify([reportSnapshot, key, reportPreview && [segment.dateRange?.period, segment.dateRange?.startDate, segment.dateRange?.endDate]]) : JSON.stringify([key, question.title, displayedSurvey?.language, revision]),
            questionId: question.questionIds?.[0] || question.filterRelationId,
            questionResponseIds: isReport ? (reportPreview ? uniqueArray(segment.values || []) : []) : ids,
            previewId: reportPreview ? [previewSession, segment.dateRange?.period, segment.dateRange?.startDate, segment.dateRange?.endDate].join(':') : undefined,
            dataSourceId,
            resultScope: key,
            segment,
            label: formatSegment(segment, { formatMessage, locale, count: null }),
            reportSnapshot: reportSnapshot && { ...reportSnapshot, segmentKey: key },
          }))}
        />
      )}

      {content.map((c, i) => (
        <Fragment key={i}>
          {i > 0 && <Divider />}
          {c}
        </Fragment>
      ))}
    </>
  );
};

export default FieldResult;
